import { Button, InputField, RadioButtons, TextArea } from '@swegon-core/uic';
import { useContext, useState } from 'react';
import {
    isValidEmail,
    textContainsUrl
} from '../shared/utility/regexValidation';
import {
    ButtonContainer,
    ContactFormContainer,
    InputFieldContainer,
    PrivacyPolicy,
    PrivacyPolicyLink
} from './ContactForm.styles';
import { Context as TranslationContext } from '../shared/contexts/TranslationContext';
import { DARK_GREEN } from '@swegon-core/uic/dist/src/theme';
import { CountryDto } from './InputFieldsContainer';
import { SelectFieldOption } from '@swegon-core/uic/dist/src/components/forms/types';

export type EmailData = Omit<ContactInfo, 'preferredType' | 'country'> & {
    country: string;
};

export type ContactFormProps = {
    onSendEmail: (emailData: EmailData) => void;
    countries: CountryDto[];
    preSelectedContactCountry: number;
};

type ContactInfo = {
    name: string;
    preferredType: 'email' | 'phone';
    emailAddress: string;
    phone: string;
    message: string;
    country: number;
    city: string;
    companyName: string;
    typeOfBusiness: string;
};

type FieldType = keyof Omit<ContactInfo, 'preferredType'>;

type ValidationResult = {
    formIsValid: boolean;
    errorFields: FieldType[];
};

type FieldErrorTextMapType = {
    [k in keyof Omit<
        ContactInfo,
        'preferredType' | 'message' | 'country' | 'typeOfBusiness'
    >]: string;
};

const OTHER_MARKET_VALUE = 0;

export const ContactForm = ({
    onSendEmail,
    countries,
    preSelectedContactCountry
}: ContactFormProps) => {
    const { l } = useContext(TranslationContext);
    const countriesToShow: SelectFieldOption[] = countries
        .map((country) => ({
            title: country.name,
            value: country.key
        }))
        .concat([
            {
                title: l('ahuDesignLite.share.contactSales.otherMarket'),
                value: OTHER_MARKET_VALUE
            }
        ]);
    const typeOfBusiness: SelectFieldOption[] = [
        {
            title: l(
                'ahuDesignLite.share.contactSales.typeOfBusiness.consultant'
            ),
            value: l(
                'ahuDesignLite.share.contactSales.typeOfBusiness.consultant'
            )
        },
        {
            title: l(
                'ahuDesignLite.share.contactSales.typeOfBusiness.contractor'
            ),
            value: l(
                'ahuDesignLite.share.contactSales.typeOfBusiness.contractor'
            )
        },
        {
            title: l(
                'ahuDesignLite.share.contactSales.typeOfBusiness.installer'
            ),
            value: l(
                'ahuDesignLite.share.contactSales.typeOfBusiness.installer'
            )
        },
        {
            title: l('ahuDesignLite.share.contactSales.typeOfBusiness.endUser'),
            value: l('ahuDesignLite.share.contactSales.typeOfBusiness.endUser')
        },
        {
            title: l('ahuDesignLite.share.contactSales.typeOfBusiness.other'),
            value: l('ahuDesignLite.share.contactSales.typeOfBusiness.other')
        }
    ];
    const [contactInfo, setContactInfo] = useState<ContactInfo>({
        name: '',
        preferredType: 'email',
        emailAddress: '',
        phone: '',
        message: '',
        city: '',
        country:
            countriesToShow.find(
                (c) => c.value === preSelectedContactCountry
            ) !== undefined
                ? preSelectedContactCountry
                : OTHER_MARKET_VALUE,
        companyName: '',
        typeOfBusiness: typeOfBusiness[0].title
    });
    const [errorFields, setErrorFields] = useState<FieldType[]>([]);

    const phoneNumberPattern = new RegExp(/^\+?\s*\d+[\s\-()\d]*$/);
    const htmlTagsPattern =
        /<([A-Za-z][A-Za-z0-9]*)\b[^>]*>(.*?)<\/\1>|<([A-Za-z][A-Za-z0-9]*)\b[^>]*\/>/;

    const containsHtml = (text: string) => htmlTagsPattern.test(text);
    const isValidName = (text: string) =>
        text.length > 0 && !containsHtml(text);
    const isValidCity = (text: string) =>
        text.length > 0 && !containsHtml(text);
    const isValidCompanyName = (text: string) =>
        text.length > 0 && !containsHtml(text);
    const isValidPhoneNumber = (phoneNumber: string) =>
        phoneNumber.length > 0 && phoneNumberPattern.test(phoneNumber);

    const isFormValid = (formToValidate: ContactInfo): ValidationResult => {
        const fieldsToValidate: (keyof FieldErrorTextMapType | 'message')[] = [
            'name',
            'message',
            'city',
            'companyName'
        ];
        if (formToValidate.preferredType === 'email') {
            fieldsToValidate.push('emailAddress');
        } else if (formToValidate.preferredType == 'phone') {
            fieldsToValidate.push('phone');
        }
        const errorFields: FieldType[] = fieldsToValidate.filter((field) => {
            switch (field) {
                case 'emailAddress':
                    if (!isValidEmail(formToValidate.emailAddress))
                        return field;
                    break;
                case 'phone':
                    if (!isValidPhoneNumber(formToValidate.phone)) return field;
                    break;
                case 'message':
                    if (textContainsUrl(formToValidate.message)) return field;
                    break;
                case 'name':
                    if (!isValidName(formToValidate.name)) return field;
                    break;
                case 'city':
                    if (!isValidCity(formToValidate.city)) return field;
                    break;
                case 'companyName':
                    if (!isValidCompanyName(formToValidate.companyName))
                        return field;
                    break;
                default:
                    throw `${field} is not supported!`;
            }
        });
        return { formIsValid: errorFields.length === 0, errorFields };
    };

    const FieldErrorTextMap: FieldErrorTextMapType = {
        emailAddress: l('ahuDesignLite.invalidEmail'),
        name: l('ahuDesignLite.missingName'),
        phone: l('ahuDesignLite.invalidPhoneNumber'),
        city: l('ahuDesignLite.share.contactSales.city.missing'),
        companyName: l('ahuDesignLite.share.contactSales.companyName.missing')
    };

    const validateForm = () => {
        const { formIsValid, errorFields } = isFormValid(contactInfo);
        if (formIsValid) {
            sendEmail();
        } else {
            setErrorFields(errorFields);
        }
    };

    const sendEmail = () => {
        const messageToSend =
            contactInfo.message === ''
                ? l('ahuDesignLite.contactMessageTemplate')
                : contactInfo.message;
        onSendEmail({
            name: contactInfo.name,
            emailAddress:
                contactInfo.preferredType == 'email'
                    ? contactInfo.emailAddress
                    : '',
            phone:
                contactInfo.preferredType == 'phone' ? contactInfo.phone : '',
            message: messageToSend,
            city: contactInfo.city,
            country:
                countriesToShow.find(
                    (country) =>
                        Number(country.value) === Number(contactInfo.country)
                )?.title ?? 'Invalid country',
            companyName: contactInfo.companyName,
            typeOfBusiness: contactInfo.typeOfBusiness
        });
    };

    const handleChange = (prop: keyof ContactInfo, value: string) => {
        const newContactInfo = {
            ...contactInfo,
            [prop]: value
        };
        const { errorFields } = isFormValid(newContactInfo);
        setErrorFields(errorFields);
        setContactInfo(newContactInfo);
    };

    const onSubmitHandler = (e: React.ChangeEvent<HTMLFormElement>) =>
        e.preventDefault();

    return (
        <ContactFormContainer key="contact-form-container">
            <form onSubmit={onSubmitHandler}>
                <InputFieldContainer>
                    <InputField
                        id="contact-form-name"
                        label={l('common.name')}
                        type="text"
                        onChange={(value) => handleChange('name', value)}
                        value={contactInfo.name}
                        errorText={
                            errorFields.includes('name')
                                ? FieldErrorTextMap['name']
                                : undefined
                        }
                    />
                </InputFieldContainer>
                <InputFieldContainer>
                    <RadioButtons
                        label={l('ahuDesignLite.contactMeBy')}
                        items={[
                            {
                                label: l('ahuDesignLite.email'),
                                value: 'email'
                            },
                            {
                                label: l('ahuDesignLite.phone'),
                                value: 'phone'
                            }
                        ]}
                        selectedValue={contactInfo.preferredType}
                        onChange={(value) =>
                            handleChange('preferredType', value)
                        }
                    />
                </InputFieldContainer>
                <InputFieldContainer>
                    {contactInfo.preferredType === 'email' && (
                        <InputField
                            id="contact-form-email"
                            label={l('ahuDesignLite.yourEmail')}
                            type="text"
                            onChange={(value) =>
                                handleChange('emailAddress', value)
                            }
                            value={contactInfo.emailAddress}
                            errorText={
                                errorFields.includes('emailAddress')
                                    ? FieldErrorTextMap['emailAddress']
                                    : undefined
                            }
                        />
                    )}
                    {contactInfo.preferredType === 'phone' && (
                        <InputField
                            id="contact-form-phone"
                            label={l('ahuDesignLite.yourPhoneNumber')}
                            type="text"
                            onChange={(value) => handleChange('phone', value)}
                            value={contactInfo.phone}
                            errorText={
                                errorFields.includes('phone')
                                    ? FieldErrorTextMap['phone']
                                    : undefined
                            }
                        />
                    )}
                </InputFieldContainer>
                <InputFieldContainer>
                    <InputField
                        id="contact-form-company-name"
                        label={l(
                            'ahuDesignLite.share.contactSales.companyName'
                        )}
                        type="text"
                        value={contactInfo.companyName}
                        onChange={(value) => handleChange('companyName', value)}
                        errorText={
                            errorFields.includes('companyName')
                                ? FieldErrorTextMap['companyName']
                                : undefined
                        }
                    />
                </InputFieldContainer>
                <InputFieldContainer>
                    <InputField
                        id="contact-form-type-of-business"
                        label={l(
                            'ahuDesignLite.share.contactSales.typeOfBusiness'
                        )}
                        type="select"
                        options={typeOfBusiness}
                        value={contactInfo.typeOfBusiness}
                        onChange={(value) =>
                            handleChange('typeOfBusiness', value)
                        }
                    />
                </InputFieldContainer>
                <InputFieldContainer>
                    <InputField
                        id="contact-form-country"
                        label={l('climate.country')}
                        type="select"
                        value={contactInfo.country}
                        options={countriesToShow}
                        onChange={(value) => handleChange('country', value)}
                    />
                </InputFieldContainer>
                <InputFieldContainer>
                    <InputField
                        label={l('ahuDesignLite.share.contactSales.city')}
                        type="text"
                        value={contactInfo.city}
                        onChange={(value) => handleChange('city', value)}
                        errorText={
                            errorFields.includes('city')
                                ? FieldErrorTextMap['city']
                                : undefined
                        }
                    />
                </InputFieldContainer>
                <InputFieldContainer>
                    <TextArea
                        id="contact-form-message"
                        value={
                            contactInfo.message.length
                                ? contactInfo.message
                                : ''
                        }
                        onChange={(value) => handleChange('message', value)}
                        label={l('ahuDesignLite.message')}
                        placeholder={l('ahuDesignLite.contactMessageTemplate')}
                        errorText={
                            textContainsUrl(contactInfo.message)
                                ? l('ahuDesignLite.messageMustNotContainUrls')
                                : undefined
                        }
                    />
                </InputFieldContainer>
                <ButtonContainer>
                    <Button
                        id={'contact-form-send-button'}
                        label={l('ahuDesign.send').toUpperCase()}
                        color={DARK_GREEN}
                        onClick={validateForm}
                    />
                </ButtonContainer>
            </form>
            <PrivacyPolicy>
                {`${l('ahuDesignLite.share.privacyPolicy')} `}
                <PrivacyPolicyLink
                    href={l('ahuDesignLite.share.privacyPolicyUrl')}
                    target="_blank"
                    referrerPolicy="no-referrer"
                >
                    {l('ahuDesignLite.share.privacyPolicyUrlText')}
                </PrivacyPolicyLink>
            </PrivacyPolicy>
            <PrivacyPolicy>
                {`${l('ahuDesignLite.share.privacyPolicyGeneral')} `}
                <PrivacyPolicyLink
                    href={l('ahuDesignLite.share.privacyPolicyGeneralUrl')}
                    target="_blank"
                    referrerPolicy="no-referrer"
                >
                    {l('ahuDesignLite.share.privacyPolicyGeneralUrlText')}
                </PrivacyPolicyLink>
            </PrivacyPolicy>
        </ContactFormContainer>
    );
};
